import { useState, useRef, useCallback } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { useApp } from '@contexts/AppContext';
import { getConsent, revokeConsent } from '@components/common/ConsentBanner';
import { logConsentChange } from '@services/api/consentService';
import Modal from '@components/common/Modal';
import Toast from '@components/common/Toast';

/**
 * ProfileEditor — Profile card with avatar + field grid
 *
 * Sections: Identity (locked) | Preferences (editable) | Consent
 *
 * B2C users: name/email locked from Azure AD B2C.
 * They CAN set avatar, nickname, display name within MRT.
 *
 * GDPR: consent checkbox + readable text popup next to it.
 * Consent changes logged via SCD audit trail (append-only).
 */

/** Data consent full text — shown in popup next to checkbox */
const CONSENT_TEXT = `By checking this box, you consent to Sandvik collecting and processing your personal data in accordance with our Privacy Policy and applicable data protection regulations (including GDPR).

Your data may be used for:
- Account management and authentication
- Order processing and delivery
- Product recommendations and personalization
- Communication about your orders and account
- Analytics to improve our services

You may withdraw your consent at any time by unchecking this box. Withdrawal of consent does not affect the lawfulness of processing based on consent before its withdrawal.

For questions about your data, contact our Data Protection Officer at privacy@sandvik.com.`;

/** Load persisted profile prefs from localStorage */
function loadProfilePrefs() {
  try {
    const raw = localStorage.getItem('mrt_profile_prefs');
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

/** Save profile prefs to localStorage */
function saveProfilePrefs(prefs) {
  try {
    localStorage.setItem('mrt_profile_prefs', JSON.stringify(prefs));
  } catch { /* quota exceeded — ignore */ }
}

export default function ProfileEditor() {
  const { account, userType } = useAuth();
  const { state, dispatch } = useApp();

  // Load persisted profile prefs (merged with account data)
  const saved = loadProfilePrefs();

  // Editable fields — prefer saved prefs, fall back to account data
  const [isEditing, setIsEditing] = useState(false);
  const [nickname, setNickname] = useState(saved.nickname ?? account?.nickname ?? '');
  const [phone, setPhone] = useState(saved.phone ?? account?.phone ?? '');
  const [language, setLanguage] = useState(state.language || 'en');
  const [shippingAddress, setShippingAddress] = useState(saved.shippingAddress ?? account?.shippingAddress ?? '');
  const [uom, setUom] = useState(state.uom || 'metric');
  const [avatarUrl, setAvatarUrl] = useState(saved.avatarUrl ?? account?.avatarUrl ?? null);

  // Consent
  const currentConsent = getConsent();
  const [consentGiven, setConsentGiven] = useState(currentConsent?.accepted ?? false);
  const [showConsentText, setShowConsentText] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  // Avatar upload
  const fileInputRef = useRef(null);

  const isB2C = userType === 'customer';
  const isLocked = !isEditing;

  const userName = account?.name || 'User';
  const userEmail = account?.email || '';
  const initials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const handleAvatarClick = () => {
    if (!isEditing) return;
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      setToast({ type: 'error', message: 'Please upload an image file.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setToast({ type: 'error', message: 'Image must be under 5 MB.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setAvatarUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleConsentChange = async (checked) => {
    setConsentGiven(checked);
    const timestamp = new Date().toISOString();

    if (!checked) {
      await revokeConsent();
    }

    // Log to SCD audit trail (append-only)
    await logConsentChange({
      consent_given: checked,
      timestamp,
      source: 'profile',
    });
  };

  const handleSave = useCallback(async () => {
    // Persist preferences to localStorage (wire to real API when available)
    saveProfilePrefs({ nickname, phone, shippingAddress, avatarUrl });
    dispatch({ type: 'SET_UOM', payload: uom });
    dispatch({ type: 'SET_LANGUAGE', payload: language });
    setIsEditing(false);
    setToast({ type: 'success', message: 'Profile updated successfully.' });
  }, [nickname, phone, shippingAddress, avatarUrl, uom, language, dispatch]);

  const handleCancel = () => {
    // Reset to last-saved values
    const prev = loadProfilePrefs();
    setPhone(prev.phone ?? account?.phone ?? '');
    setLanguage(state.language || 'en');
    setShippingAddress(prev.shippingAddress ?? account?.shippingAddress ?? '');
    setUom(state.uom || 'metric');
    setNickname(prev.nickname ?? account?.nickname ?? '');
    setAvatarUrl(prev.avatarUrl ?? account?.avatarUrl ?? null);
    setIsEditing(false);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Avatar + Name */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <button
              onClick={handleAvatarClick}
              disabled={!isEditing}
              className={`
                w-20 h-20 rounded-full flex items-center justify-center
                text-xl font-bold text-white overflow-hidden
                ${isEditing ? 'cursor-pointer ring-2 ring-electric-blue' : 'cursor-default'}
                ${avatarUrl ? '' : 'bg-n4'}
              `}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </button>
            {isEditing && (
              <span className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full
                               flex items-center justify-center text-white text-xs">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
              </span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>
          <div>
            <h2 className="font-sandvik-headline font-bold text-xl text-carbon-black">
              {userName}
            </h2>
            <p className="text-sm text-n5">{userEmail}</p>
          </div>
        </div>

        {/* Identity Section — locked for B2C */}
        <section>
          <h3 className="text-xs font-medium text-n5 tracking-wider mb-3 border-b border-n2 pb-2">
            Identity
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldRow label="Name" value={userName} locked />
            <FieldRow label="Email" value={userEmail} locked />
            {isB2C && (
              <FieldRow
                label="Display name"
                value={nickname}
                onChange={(v) => setNickname(v)}
                locked={isLocked}
                placeholder="Set a display name"
              />
            )}
          </div>
        </section>

        {/* Preferences Section — editable */}
        <section>
          <h3 className="text-xs font-medium text-n5 tracking-wider mb-3 border-b border-n2 pb-2">
            Preferences
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldRow
              label="Phone"
              value={phone}
              onChange={(v) => setPhone(v)}
              locked={isLocked}
              placeholder="+1 555-0172"
            />
            <div>
              <label className="block text-sm font-medium text-n6 mb-1">Language</label>
              {isLocked ? (
                <p className="text-sm text-carbon-black py-2">{language === 'en' ? 'English' : language}</p>
              ) : (
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full border border-n3 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                >
                  <option value="en">English</option>
                  <option value="fr">French</option>
                  <option value="es">Spanish</option>
                  <option value="pt">Portuguese</option>
                  <option value="sv">Swedish</option>
                </select>
              )}
            </div>
            <FieldRow
              label="Shipping address"
              value={shippingAddress}
              onChange={(v) => setShippingAddress(v)}
              locked={isLocked}
              placeholder="Enter shipping address"
            />
            <div>
              <label className="block text-sm font-medium text-n6 mb-1">Unit of measurement</label>
              {isLocked ? (
                <p className="text-sm text-carbon-black py-2">{uom === 'metric' ? 'Metric' : 'Imperial'}</p>
              ) : (
                <select
                  value={uom}
                  onChange={(e) => setUom(e.target.value)}
                  className="w-full border border-n3 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                >
                  <option value="metric">Metric</option>
                  <option value="imperial">Imperial</option>
                </select>
              )}
            </div>
          </div>
        </section>

        {/* Consent Section */}
        <section>
          <h3 className="text-xs font-medium text-n5 tracking-wider mb-3 border-b border-n2 pb-2">
            Consent
          </h3>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="data-consent"
              checked={consentGiven}
              onChange={(e) => handleConsentChange(e.target.checked)}
              className="mt-1 w-4 h-4 text-electric-blue rounded border-n3
                         focus:ring-electric-blue"
            />
            <div>
              <label htmlFor="data-consent" className="text-sm text-carbon-black cursor-pointer">
                I consent to data processing
              </label>
              <button
                onClick={() => setShowConsentText(true)}
                className="block text-xs text-electric-blue hover:underline mt-1"
              >
                Read full data consent text
              </button>
            </div>
          </div>
        </section>

        {/* Action Buttons */}
        <div className="flex justify-center gap-3 pt-4">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className="px-6 py-2.5 border border-n3 rounded text-sm font-medium
                           text-carbon-black hover:bg-n1 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2.5 bg-electric-blue text-white rounded text-sm font-medium
                           hover:bg-blue-dark transition-colors"
              >
                Save changes
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-6 py-2.5 bg-electric-blue text-white rounded text-sm font-medium
                         hover:bg-blue-dark transition-colors"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Consent Text Popup */}
      <Modal
        isOpen={showConsentText}
        onClose={() => setShowConsentText(false)}
        title="Data consent"
        size="md"
        footer={
          <div className="flex justify-center">
            <button
              onClick={() => setShowConsentText(false)}
              className="px-6 py-2.5 border border-n3 rounded text-sm font-medium
                         text-carbon-black hover:bg-n1 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        <div className="prose prose-sm text-n6 whitespace-pre-line">
          {CONSENT_TEXT}
        </div>
      </Modal>

      {/* Toast */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}

/** Reusable field row for locked/editable display */
function FieldRow({ label, value, onChange, locked = false, placeholder = '' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-n6 mb-1">{label}</label>
      {locked ? (
        <p className="text-sm text-carbon-black py-2">{value || '—'}</p>
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          className="w-full border border-n3 rounded px-3 py-2 text-sm
                     focus:ring-2 focus:ring-electric-blue focus:border-transparent"
        />
      )}
    </div>
  );
}
